// LessonHeader.tsx
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, InformationFilled } from '@teo/components/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { IconButton, Dropdown, PersonAvatar, Button } from '@teo/components';
import { NotificationButton } from '@/components/Notifications/NotificationButton';
import { axios } from '@/axios';
import { config } from '@/config';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import i18n from 'i18next';
import { logout } from '@/services/logout.service.js';
import { Timer } from './Timer'; // ✅ no .tsx extension

type TimerMode = 'up' | 'down';

const LessonHeader = ({
  coursePath,
  student,
  course,
  setHideContent,
  hideContent,
  timeDisabled,
  activeLesson,
  timeSpent,
  userId,
  submitFn,
  onCourseExpire,
  onLessonExpire,
  returnPath,
  isPreview,
  nextLesson,
  activeStatus,
  couresPreview,
  activeNode,
  isTeacher,
  viewMobile,
  setShowExtraInfo,
  setExtraInfoFullScreen,
  extraInfoFullScreen,
  setActiveExtraInfo,
  lengthExtraInfo,
  iSsmallLaptop,
  setCourseExpiredLocal,
  showAnswers,
}: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organisationId } = useParams();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });

  const [createStatus, setCreateStatus] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  const prevPage = queryParams.get('details');
  const newTab = queryParams.get('new_tab');
  const lpgroupId = queryParams.get('lpgroupId');
  const lesId = queryParams.get('les');

  const userFollowPage = `/${i18n.language}/follow-up/${organisationId}/users/${userId}`;
  const userCorrectionPage = `/${i18n.language}/follow-up/${organisationId}/learning-paths/correction/${lpgroupId}/${userId}`;

  const timedCourse = !!course?.courseTimer;
  const canRunTimers = !isTeacher && !isPreview;

  // --- serverOffsetMs ---
  const [serverOffsetMs, setServerOffsetMs] = useState<number | null>(null);

  useEffect(() => {
    const dbNowStr = student?.dbNow;
    const dbNowMs = dbNowStr ? Date.parse(dbNowStr) : NaN;
    setServerOffsetMs(Number.isFinite(dbNowMs) ? dbNowMs - Date.now() : null);
  }, [student?.dbNow]);

  // --- effectiveStartAtMs: if startedAt missing, use dbNow ---
  const effectiveStartAtMs = useMemo(() => {
    const startedAtStr = student?.courseTimerStartedAt;
    const dbNowStr = student?.dbNow;

    const startedAtMs = startedAtStr ? Date.parse(startedAtStr) : NaN;
    if (Number.isFinite(startedAtMs)) return startedAtMs;

    const dbNowMs = dbNowStr ? Date.parse(dbNowStr) : NaN;
    return Number.isFinite(dbNowMs) ? dbNowMs : null;
  }, [student?.courseTimerStartedAt, student?.dbNow]);

  // --- endAtMs ---
  const courseEndAtMs = useMemo(() => {
    if (!timedCourse) return null;
    if (effectiveStartAtMs == null) return null;
    const totalSeconds = Number(course?.courseTimer ?? 0);
    if (!Number.isFinite(totalSeconds)) return null;
    return effectiveStartAtMs + totalSeconds * 1000;
  }, [timedCourse, course?.courseTimer, effectiveStartAtMs]);

  // Reset “status created” flag when switching lessons
  useEffect(() => {
    setCreateStatus(false);
  }, [activeLesson?.lessonId]);

  // treat limited as countdown only if it has a positive estimated time
  const estimatedLessonSeconds = Number(activeLesson?.estimatedTime ?? 0);

  const lessonTimerMode: TimerMode | null =
    activeLesson?.timed === 'limited' && estimatedLessonSeconds > 0
      ? 'down'
      : activeLesson?.timed === 'timed'
      ? 'up'
      : null;

  const lessonInitialSeconds = useMemo(() => {
    if (!activeLesson || !lessonTimerMode) return 0;

    if (lessonTimerMode === 'down') {
      const spent = Number(timeSpent ?? 0);
      return Math.max(0, estimatedLessonSeconds - spent);
    }

    return Math.max(0, Number(timeSpent ?? 0));
  }, [
    activeLesson?.lessonId,
    activeLesson?.timed,
    estimatedLessonSeconds,
    timeSpent,
    lessonTimerMode,
  ]);

  const sendLessonTimeRequest = (totalSeconds: number) => {
    // totalSeconds = timeSpent for the lesson (NOT remaining)
    if (activeStatus?.status || createStatus) {
      axios
        .put(
          `${config.backend}/lessonstatus/${activeLesson?.courseId}/${userId}/${activeLesson?.lessonId}`,
          {
            courseId: activeLesson?.courseId,
            lessonId: activeLesson?.lessonId,
            timeSpent: totalSeconds,
            userId: userId,
          }
        )
        .catch((e) =>
          console.error('[LessonHeader] lesson time PUT failed', e)
        );
    } else {
      // This can race LessonPage's own (properly versioned) POST that
      // creates the same lessonstatuses row when a lesson is first opened —
      // whichever insert wins the unique constraint sticks. Include
      // `version` here too so that, whichever one wins, the row still ends
      // up pinned to the content version the student actually started on,
      // instead of silently staying unpinned (version NULL) forever.
      axios
        .post(`${config.backend}/lessonstatuses`, {
          courseId: activeLesson?.courseId,
          lessonId: activeLesson?.lessonId,
          status: 'started',
          userId: userId,
          version: activeLesson?.version,
        })
        .catch((e) =>
          console.error('[LessonHeader] lesson status POST failed', e)
        );
      setCreateStatus(true);
    }
  };

  const reportLessonTick = (payload: { seconds: number; mode: TimerMode }) => {
    if (!activeLesson?.lessonId || timeDisabled) return;

    if (payload.mode === 'up') {
      sendLessonTimeRequest(Math.floor(payload.seconds));
      return;
    }

    const est = Number(activeLesson?.estimatedTime ?? 0);
    const remaining = Math.max(0, Math.floor(payload.seconds));
    const spent = Math.max(0, est - remaining);
    sendLessonTimeRequest(spent);
  };

  const handleLessonTimerExpire = () => {
    if (!activeLesson?.lessonId) return;

    const est = Number(activeLesson?.estimatedTime ?? 0);
    sendLessonTimeRequest(est);

    if (onLessonExpire) {
      onLessonExpire();
      return;
    }

    submitFn?.();
  };

  return (
    <header
      className="sticky top-0 z-[31] flex h-[80px] min-h-16 items-center justify-start overflow-y-hidden border-b border-solid border-grey-02 bg-white p-3"
      data-testid="header"
    >
      {!isPreview && (
        <>
          {!extraInfoFullScreen ? (
            <>
              {!queryParams.get('tincan') && (
                <>
                  <div data-testid="backOnCourse">
                    {!isMobile && !viewMobile && (
                      <IconButton
                        className={`my-auto mx-2 ${newTab && '!hidden'}`}
                        Icon={ArrowLeft}
                        variant="outline"
                        onClick={() => {
                          navigate(
                            returnPath
                              ? returnPath
                              : couresPreview
                              ? showAnswers
                                ? lesId && lesId != '0'
                                  ? `${coursePath}?les=${lesId}`
                                  : coursePath
                                : `${coursePath}?les=${
                                    activeNode?.usesId ? activeNode?.usesId : 0
                                  }`
                              : prevPage
                              ? userFollowPage
                              : lpgroupId
                              ? userCorrectionPage
                              : coursePath
                          );
                        }}
                      />
                    )}
                    {(isMobile || viewMobile) && (
                      <IconButton
                        className={`my-auto mx-2  ${newTab && '!hidden'}`}
                        Icon={ArrowLeft}
                        variant="ghost"
                        onClick={() =>
                          navigate(
                            returnPath
                              ? returnPath
                              : couresPreview
                              ? `${coursePath}?les=${
                                  activeNode?.usesId ? activeNode?.usesId : 0
                                }`
                              : prevPage
                              ? userFollowPage
                              : coursePath
                          )
                        }
                      />
                    )}
                  </div>
                  <b>{isTeacher ? student?.username : null}</b>
                </>
              )}
            </>
          ) : (
            <Button
              className="flex items-center gap-3"
              variant="text"
              onClick={() => {
                setShowExtraInfo(false);
                setExtraInfoFullScreen(false);
                setActiveExtraInfo(null);
              }}
            >
              <ArrowLeft className="h-5 w-5" />
              <p>
                {t('extra_info.back_to_les')} {activeLesson?.name}
              </p>
            </Button>
          )}
        </>
      )}

      {student && (
        <div
          className="relative mr-1 ml-1 h-11 w-11 min-w-[44px] rounded-full text-center sm:ml-3 sm:mr-3"
          style={{
            background: `radial-gradient(closest-side, white 85%, transparent 90% 100%),conic-gradient(#3257fc ${
              student.progress ? Math.round(student.progress * 100) : 0
            }%, #1e1e2014 0)`,
          }}
        >
          <p className="pt-3 text-sm text-grey-07">
            {student.progress ? Math.round(student.progress * 100) : 0}&#37;
          </p>
        </div>
      )}

      <p className="mx-1 text-sm font-medium text-grey-08">
        {course?.courseName || course?.name || ''}
      </p>

      {/* Timers */}
      <div className="ml-auto flex items-center gap-3">
        {/* Lesson timer */}
        {lessonTimerMode === 'down' &&
          canRunTimers &&
          !timeDisabled &&
          lessonInitialSeconds > 0 && (
            <span className="rounded bg-error-01 py-1 px-2 text-sm font-medium text-error-04">
              <Timer
                mode="down"
                initialSeconds={lessonInitialSeconds}
                running={!timeDisabled}
                intervalSeconds={30}
                onInterval={({
                  seconds,
                  mode,
                }: {
                  seconds: number;
                  mode: TimerMode;
                }) => reportLessonTick({ seconds, mode })}
                onExpire={handleLessonTimerExpire}
                resetOnInitialChange={true}
              />
            </span>
          )}

        {lessonTimerMode === 'up' && canRunTimers && !timeDisabled && (
          <span className="text-sm font-medium">
            <Timer
              mode="up"
              initialSeconds={lessonInitialSeconds}
              running={!timeDisabled}
              intervalSeconds={30}
              onInterval={({
                seconds,
                mode,
              }: {
                seconds: number;
                mode: TimerMode;
              }) => reportLessonTick({ seconds, mode })}
              resetOnInitialChange={true}
            />
          </span>
        )}

        {/* Course timer */}
        {timedCourse && canRunTimers && courseEndAtMs != null && (
          <span className="rounded bg-error-01 py-1 px-2 text-sm font-medium text-error-04">
            <Timer
              mode="down"
              endAtMs={courseEndAtMs}
              nowProvider={
                serverOffsetMs != null
                  ? () => Date.now() + serverOffsetMs
                  : undefined
              }
              running={true}
              intervalSeconds={1}
              onExpire={onCourseExpire}
              resetOnInitialChange={true}
            />
          </span>
        )}
      </div>

      {(iSsmallLaptop || viewMobile) &&
        (!hideContent || !isMobile) &&
        lengthExtraInfo > 0 &&
        !activeLesson?.assetId && (
          <div
            className="relative ml-auto"
            onClick={() => setShowExtraInfo((prev: any) => !prev)}
          >
            <InformationFilled className="mr-4 h-6 w-6 text-grey-07" />
            <div className="absolute -top-1 right-3 h-2 w-2 rounded-full bg-error-04"></div>
          </div>
        )}

      {authUser && !isPreview && (
        <div
          className={`${
            !iSsmallLaptop && !viewMobile && 'ml-auto '
          } flex flex-row gap-4`}
        >
          <NotificationButton />
          {!isMobile && !viewMobile && (
            <Dropdown>
              <Dropdown.Trigger>
                <PersonAvatar
                  id={authUser.id}
                  name={authUser.username as string}
                  imageSrc={authUser.picture || undefined}
                />
              </Dropdown.Trigger>
              <Dropdown.Content>
                <Dropdown.Section className="flex items-center gap-3">
                  <PersonAvatar
                    id={authUser.id}
                    name={authUser.username as string}
                    imageSrc={authUser.picture || undefined}
                  />
                  {authUser?.username}
                </Dropdown.Section>
                <Dropdown.Actions>
                  <Dropdown.Link to={'/' + i18n.language + '/profile/details'}>
                    {t('links.profile')}
                  </Dropdown.Link>
                  <Dropdown.ExternalLink
                    href={t('external_links.help') as string}
                    target="_blank"
                  >
                    {t('links.help')}
                  </Dropdown.ExternalLink>
                  <Dropdown.Button
                    onClick={() => {
                      logout();
                    }}
                  >
                    {t('links.logout')}
                  </Dropdown.Button>
                </Dropdown.Actions>
              </Dropdown.Content>
            </Dropdown>
          )}
          {(isMobile || viewMobile) && (
            <Button
              className="m !p-2"
              variant="ghost"
              onClick={() => {
                setHideContent(true);
              }}
            >
              <img
                src="/images/icons/list.svg"
                alt="chatgpt"
                className=" w-6"
              />
            </Button>
          )}
        </div>
      )}
    </header>
  );
};

export default LessonHeader;
